import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, AlertTriangle, Wrench, Droplet, Zap, Gauge, Clock, 
  Car, Thermometer, Battery, Disc, Cog, Settings2, ChevronDown, ChevronUp, Info,
  TrendingUp, Cpu, DollarSign
} from 'lucide-react';
import VehicleImage from '../components/ui/VehicleImage';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section Component with collapsible content
const SpecSection = ({ icon: Icon, title, children, defaultOpen = true, color = "#7CD4FD" }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const hasContent = React.Children.toArray(children).some(child => child);
  if (!hasContent) return null;
  
  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 rounded-xl transition-colors hover:bg-white/5"
        style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
      >
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${color}20` }}>
            <Icon size={20} style={{ color }} />
          </div>
          <span className="font-semibold text-white">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp size={20} className="text-gray-400" />
        ) : (
          <ChevronDown size={20} className="text-gray-400" />
        )}
      </button>
      
      {isOpen && (
        <div className="mt-3 pl-14 pr-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {children}
        </div>
      )}
    </div>
  );
};

// Single Spec Item
const SpecItem = ({ label, value, icon: Icon }) => {
  if (!value) return null;
  
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
      {Icon && <Icon size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
        <p className="text-sm text-white font-medium">{value}</p>
      </div>
    </div>
  );
};

// Wear Part Item
const WearPartItem = ({ part }) => (
  <div className="col-span-2 flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
    <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
    <span className="text-sm text-gray-300">{part}</span>
  </div>
);

export const VehicleDataPage = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVehicleData();
  }, [vehicleId]);

  const fetchVehicleData = async () => {
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/vehicle-data/specifications/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicleData(response.data);
    } catch (error) {
      console.error('VehicleData error:', error);
      if (error.response?.status === 403) {
        setError({
          type: 'access',
          message: error.response?.data?.detail || 'Diese Funktion ist in Ihrem Abo-Plan nicht enthalten'
        });
      } else if (error.response?.status === 404) {
        setError({
          type: 'not_found',
          message: 'Fahrzeug nicht gefunden'
        });
      } else {
        setError({
          type: 'error',
          message: error.response?.data?.detail || 'Fehler beim Laden der Fahrzeugdaten'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8 flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="max-w-4xl mx-auto">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-6 text-gray-300">
            <ArrowLeft className="mr-2" size={16} />
            Zurück
          </Button>

          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-8 text-center">
              <Info size={48} className="mx-auto mb-4 text-amber-500" />
              <h3 className="text-xl font-semibold mb-2 text-white">
                {error.type === 'access' ? 'Zugriff eingeschränkt' : 'Fehler'}
              </h3>
              <p className="text-gray-400 mb-4">
                {error.message}
              </p>
              {error.type === 'access' && (
                <Button 
                  onClick={() => navigate('/subscription')} 
                  className="mt-4"
                  style={{ backgroundColor: '#1EA0E6' }}
                >
                  Abo upgraden
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!vehicleData?.available) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="max-w-4xl mx-auto">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-6 text-gray-300">
            <ArrowLeft className="mr-2" size={16} />
            Zurück
          </Button>

          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-8 text-center">
              <Info size={48} className="mx-auto mb-4 text-gray-500" />
              <h3 className="text-xl font-semibold mb-2 text-white">
                Daten noch nicht verfügbar
              </h3>
              <p className="text-gray-400">
                {vehicleData?.message}
              </p>
              {vehicleData?.vehicle && (
                <p className="mt-4 text-sm text-cyan-400">
                  Fahrzeug: {vehicleData.vehicle.brand} {vehicleData.vehicle.model}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const specs = vehicleData.specifications;
  const vehicle = vehicleData.vehicle;
  // Handle both old format (0-1) and new format (0-100)
  const rawCompleteness = vehicleData.completeness || 0;
  const completeness = rawCompleteness > 1 ? rawCompleteness : rawCompleteness * 100;

  return (
    <div className="min-h-screen p-6 md:p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-5xl mx-auto">
        <Button onClick={() => navigate(-1)} variant="ghost" className="mb-6 text-gray-300 hover:text-white">
          <ArrowLeft className="mr-2" size={16} />
          Zurück
        </Button>

        {/* Header Card with Vehicle Image */}
        <Card 
          className="mb-6 overflow-hidden"
          style={{ 
            backgroundColor: '#0F141A', 
            borderColor: 'rgba(30,160,230,0.3)',
            background: 'linear-gradient(180deg, rgba(30,160,230,0.1) 0%, #0F141A 100%)'
          }}
        >
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Vehicle Image - Using VehicleImage component for CORS handling */}
              <div className="flex-shrink-0">
                <VehicleImage 
                  vehicleId={vehicleId}
                  brand={vehicle.brand}
                  model={vehicle.model}
                  size="hero"
                  className="w-56 h-36"
                />
              </div>
              
              {/* Vehicle Info */}
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-1">
                  {vehicle.brand} {vehicle.model}
                </h1>
                {vehicle.engine && (
                  <p className="text-cyan-400 text-lg mb-4">{vehicle.engine}</p>
                )}
                
                {/* Completeness Bar */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1 h-2.5 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${completeness}%`,
                        backgroundColor: completeness >= 80 ? '#22C55E' : completeness >= 50 ? '#F59E0B' : '#EF4444'
                      }}
                    />
                  </div>
                  <span className="text-sm text-gray-400 whitespace-nowrap">
                    {completeness.toFixed(0)}% Datenvollständigkeit
                  </span>
                </div>
                
                {/* Quick Stats */}
                <div className="flex flex-wrap gap-3">
                  {specs?.engine?.power && (
                    <div className="px-3 py-1.5 rounded-lg bg-purple-500/20 border border-purple-500/30">
                      <span className="text-xs text-purple-300">{specs.engine.power}</span>
                    </div>
                  )}
                  {specs?.engine?.torque && (
                    <div className="px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30">
                      <span className="text-xs text-blue-300">{specs.engine.torque}</span>
                    </div>
                  )}
                  {specs?.engine?.displacement && (
                    <div className="px-3 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-500/30">
                      <span className="text-xs text-cyan-300">{specs.engine.displacement}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <div 
          className="mb-6 p-4 rounded-xl flex items-start gap-3"
          style={{ backgroundColor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}
        >
          <AlertTriangle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-yellow-500 mb-1">Wichtiger Hinweis</p>
            <p className="text-xs text-gray-300">
              {vehicleData.disclaimer || "Alle Angaben ohne Gewähr. Bitte immer die offiziellen Herstellerangaben prüfen."}
            </p>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div>
            {/* Motor / Leistung */}
            <SpecSection icon={Gauge} title="Motorleistung" color="#22D3EE">
              <SpecItem label="Leistung" value={specs?.engine?.power} />
              <SpecItem label="Drehmoment" value={specs?.engine?.torque} />
              <SpecItem label="Hubraum" value={specs?.engine?.displacement} />
              <SpecItem label="Zylinder" value={specs?.engine?.cylinder_count?.toString()} />
              <SpecItem label="Verdichtung" value={specs?.engine?.compression_ratio} />
              <SpecItem label="Turbo" value={specs?.engine?.turbocharger ? "Ja" : specs?.engine?.turbocharger === false ? "Nein" : null} />
            </SpecSection>
            
            {/* Füllmengen */}
            <SpecSection icon={Droplet} title="Füllmengen" color="#3B82F6">
              <SpecItem label="Motoröl" value={specs?.engine?.oil_capacity} icon={Droplet} />
              <SpecItem label="Ölsorte" value={specs?.engine?.oil_type} />
              <SpecItem label="Kühlmittel" value={specs?.engine?.coolant_capacity} icon={Thermometer} />
              <SpecItem label="Kühlmittel-Typ" value={specs?.engine?.coolant_type} />
              <SpecItem label="Getriebeöl" value={specs?.transmission?.transmission_oil_capacity} icon={Cog} />
              <SpecItem label="Bremsflüssigkeit" value={specs?.braking_system?.brake_fluid_type} />
            </SpecSection>
            
            {/* Klimaanlage */}
            <SpecSection icon={Zap} title="Klimaanlage" color="#A78BFA" defaultOpen={false}>
              <SpecItem label="Kältemittel" value={specs?.air_conditioning?.refrigerant_type} />
              <SpecItem label="Füllmenge" value={specs?.air_conditioning?.refrigerant_amount} />
              <SpecItem label="Kompressor-Öl" value={specs?.air_conditioning?.compressor_oil_type} />
              <SpecItem label="Öl-Menge" value={specs?.air_conditioning?.compressor_oil_amount} />
            </SpecSection>
          </div>
          
          {/* Right Column */}
          <div>
            {/* Drehmomente */}
            <SpecSection icon={Wrench} title="Anzugsdrehmomente" color="#F59E0B">
              <SpecItem label="Radschrauben" value={specs?.torque_specs?.wheel_bolts} icon={Disc} />
              <SpecItem label="Ölablassschraube" value={specs?.torque_specs?.oil_drain_plug} />
              <SpecItem label="Zündkerzen" value={specs?.torque_specs?.spark_plugs} />
              <SpecItem label="Zylinderkopf" value={specs?.torque_specs?.cylinder_head_bolts} />
              <SpecItem label="Ölfilter" value={specs?.torque_specs?.oil_filter} />
              <SpecItem label="Getriebeablassschraube" value={specs?.torque_specs?.transmission_drain_plug} />
            </SpecSection>
            
            {/* Verschleißteile */}
            {specs?.common_repairs?.typical_wear_parts?.length > 0 && (
              <SpecSection icon={AlertTriangle} title="Häufige Verschleißteile" color="#EF4444">
                {specs.common_repairs.typical_wear_parts.slice(0, 6).map((part, idx) => (
                  <WearPartItem key={idx} part={part} />
                ))}
              </SpecSection>
            )}
            
            {/* Service-Intervalle */}
            <SpecSection icon={Clock} title="Service-Intervalle" color="#22C55E" defaultOpen={false}>
              <SpecItem label="Ölwechsel" value={specs?.service_intervals?.oil_change} />
              <SpecItem label="Luftfilter" value={specs?.service_intervals?.air_filter} />
              <SpecItem label="Kraftstofffilter" value={specs?.service_intervals?.fuel_filter} />
              <SpecItem label="Zündkerzen" value={specs?.service_intervals?.spark_plugs} />
              <SpecItem label="Zahnriemen" value={specs?.service_intervals?.timing_belt} />
              <SpecItem label="Bremsflüssigkeit" value={specs?.service_intervals?.brake_fluid} />
            </SpecSection>
            
            {/* Elektrisches System */}
            <SpecSection icon={Battery} title="Elektrisches System" color="#EC4899" defaultOpen={false}>
              <SpecItem label="Batterie" value={specs?.electrical_system?.battery_capacity} />
              <SpecItem label="Batterie-Typ" value={specs?.electrical_system?.battery_type} />
              <SpecItem label="Generator" value={specs?.electrical_system?.alternator_output} />
              <SpecItem label="Spannung" value={specs?.electrical_system?.battery_voltage} />
            </SpecSection>
          </div>
        </div>
        
        {/* Tuning Section - Full Width */}
        {vehicleData.tuning?.available && (
          <Card 
            className="mt-6"
            style={{ 
              backgroundColor: '#0F141A', 
              borderColor: 'rgba(34, 197, 94, 0.3)',
              background: 'linear-gradient(180deg, rgba(34, 197, 94, 0.05) 0%, #0F141A 100%)'
            }}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
                  <TrendingUp size={24} className="text-green-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Chiptuning Optionen</h2>
                  <p className="text-sm text-gray-400">Leistungssteigerung für Ihr Fahrzeug</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Original */}
                <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(148, 163, 184, 0.1)', border: '1px solid rgba(148, 163, 184, 0.2)' }}>
                  <p className="text-sm text-gray-400 mb-2">Original</p>
                  <p className="text-2xl font-bold text-gray-300">
                    {vehicleData.tuning.original?.power_ps || '-'} <span className="text-sm font-normal">PS</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {vehicleData.tuning.original?.torque_nm || '-'} Nm
                  </p>
                </div>
                
                {/* Stage 1 */}
                {vehicleData.tuning.stage1?.power_ps && (
                  <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                    <p className="text-sm text-green-400 mb-2">Stage 1</p>
                    <p className="text-2xl font-bold text-green-400">
                      {vehicleData.tuning.stage1.power_ps} <span className="text-sm font-normal">PS</span>
                    </p>
                    <p className="text-sm text-green-300">
                      {vehicleData.tuning.stage1.torque_nm || '-'} Nm
                    </p>
                    {vehicleData.tuning.stage1.price_avg && (
                      <p className="text-xs text-gray-400 mt-2">
                        ab CHF {vehicleData.tuning.stage1.price_avg}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Stage 2 */}
                {vehicleData.tuning.stage2?.power_ps && (
                  <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                    <p className="text-sm text-amber-400 mb-2">Stage 2</p>
                    <p className="text-2xl font-bold text-amber-400">
                      {vehicleData.tuning.stage2.power_ps} <span className="text-sm font-normal">PS</span>
                    </p>
                    <p className="text-sm text-amber-300">
                      {vehicleData.tuning.stage2.torque_nm || '-'} Nm
                    </p>
                    {vehicleData.tuning.stage2.price_avg && (
                      <p className="text-xs text-gray-400 mt-2">
                        ab CHF {vehicleData.tuning.stage2.price_avg}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* ECU Info */}
              {vehicleData.tuning.ecu?.name && (
                <div className="flex items-center gap-3 p-3 rounded-xl mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <Cpu size={18} className="text-cyan-400" />
                  <div>
                    <p className="text-sm text-gray-400">ECU / Steuergerät</p>
                    <p className="text-white font-medium">{vehicleData.tuning.ecu.name}</p>
                    {vehicleData.tuning.ecu.methods?.length > 0 && (
                      <p className="text-xs text-gray-500">
                        Verfügbare Methoden: {vehicleData.tuning.ecu.methods.map(m => m.method).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Tuning Options */}
              {vehicleData.tuning.options?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-400 mb-3">Zusätzliche Optionen</p>
                  <div className="flex flex-wrap gap-2">
                    {vehicleData.tuning.options.slice(0, 8).map((opt, idx) => (
                      <div 
                        key={idx}
                        className="px-3 py-1.5 rounded-lg text-xs"
                        style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                      >
                        <span className="text-white">{opt.name}</span>
                        {opt.price_avg && (
                          <span className="text-gray-500 ml-2">+CHF {opt.price_avg}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Last Updated */}
        {vehicleData.last_updated && (
          <p className="text-xs text-gray-500 text-center mt-8">
            Zuletzt aktualisiert: {new Date(vehicleData.last_updated).toLocaleDateString('de-DE')}
          </p>
        )}
      </div>
    </div>
  );
};

export default VehicleDataPage;
